import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, SignOut } from "@phosphor-icons/react";

export default function ChangePassword() {
  const { user, loading, refresh, logout } = useAuth();
  const nav = useNavigate();
  const [current, setCurrent] = useState("");
  const [next1, setNext1] = useState("");
  const [next2, setNext2] = useState("");
  const [busy, setBusy] = useState(false);

  if (loading || user === null) {
    return <div className="flex items-center justify-center h-screen text-zinc-500">Lade…</div>;
  }
  if (!user) return <Navigate to="/login" replace />;
  // Bereits geändert → zurück zum Dashboard
  if (!user.must_change_password) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e?.preventDefault();
    if (!current || !next1 || !next2) {
      toast.error("Bitte alle Felder ausfüllen.");
      return;
    }
    if (next1.length < 8) {
      toast.error("Neues Passwort muss mindestens 8 Zeichen lang sein.");
      return;
    }
    if (next1 !== next2) {
      toast.error("Die beiden neuen Passwörter stimmen nicht überein.");
      return;
    }
    if (next1 === current) {
      toast.error("Das neue Passwort muss sich vom aktuellen unterscheiden.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next1,
      });
      toast.success("Passwort erfolgreich geändert");
      await refresh();
      nav("/", { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Passwortwechsel fehlgeschlagen";
      toast.error(typeof msg === "string" ? msg : "Passwortwechsel fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-sm bg-[#002FA7] flex items-center justify-center">
              <ShieldCheck size={20} weight="bold" color="white" />
            </div>
            <span className="font-heading font-bold text-lg">NutriCalc</span>
          </div>
          <button
            type="button"
            onClick={logout}
            className="flex items-center gap-1 text-xs uppercase tracking-wider text-zinc-500 hover:text-zinc-900 transition-colors"
            data-testid="change-password-logout"
          >
            <SignOut size={14} weight="bold" /> Abmelden
          </button>
        </div>

        <div className="border-l-4 border-amber-500 bg-amber-50 px-4 py-3 mb-6">
          <div className="text-xs uppercase tracking-widest text-amber-700 mb-1">Aktion erforderlich</div>
          <div className="text-sm text-amber-900">
            Du bist mit einem Standard-Passwort angemeldet. Aus Sicherheitsgründen musst du jetzt ein eigenes Passwort vergeben, bevor du fortfahren kannst.
          </div>
        </div>

        <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Passwort ändern</div>
        <h1 className="font-heading text-3xl font-bold tracking-tight mb-1">
          Willkommen, {user.full_name?.split(" ")[0] || user.username}
        </h1>
        <p className="text-sm text-zinc-500 mb-8">
          Wähle ein sicheres Passwort mit mindestens 8 Zeichen.
        </p>

        <form onSubmit={submit} className="space-y-4" data-testid="change-password-form">
          <div>
            <Label htmlFor="current" className="text-xs uppercase tracking-wider text-zinc-600">
              Aktuelles Passwort
            </Label>
            <Input
              id="current"
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              autoComplete="current-password"
              className="mt-1 rounded-sm"
              data-testid="change-password-current"
            />
          </div>
          <div>
            <Label htmlFor="next1" className="text-xs uppercase tracking-wider text-zinc-600">
              Neues Passwort
            </Label>
            <Input
              id="next1"
              type="password"
              value={next1}
              onChange={(e) => setNext1(e.target.value)}
              autoComplete="new-password"
              className="mt-1 rounded-sm"
              data-testid="change-password-new"
            />
            <div className="mt-1 text-[11px] text-zinc-400">Mindestens 8 Zeichen</div>
          </div>
          <div>
            <Label htmlFor="next2" className="text-xs uppercase tracking-wider text-zinc-600">
              Neues Passwort bestätigen
            </Label>
            <Input
              id="next2"
              type="password"
              value={next2}
              onChange={(e) => setNext2(e.target.value)}
              autoComplete="new-password"
              className="mt-1 rounded-sm"
              data-testid="change-password-confirm"
            />
          </div>
          <Button
            type="submit"
            disabled={busy}
            className="w-full bg-[#002FA7] hover:bg-[#00248a] rounded-sm h-10"
            data-testid="change-password-submit"
          >
            {busy ? "Speichern…" : "Passwort speichern"}
          </Button>
        </form>
      </div>
    </div>
  );
}
